import type { FrameRate } from './frameRates';

export function frameIndexInSecond(nowMs: number, rate: FrameRate): number {
  const boxes = Math.ceil(rate.fps);
  const totalFrames = Math.floor((nowMs * rate.numerator) / (rate.denominator * 1000));
  const idx = totalFrames % boxes;
  return idx < 0 ? idx + boxes : idx;
}

export interface SmpteTc {
  hours: number;
  minutes: number;
  seconds: number;
  frames: number;
  separator: ':' | ';';
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function smpteTimecode(nowMs: number, rate: FrameRate): SmpteTc {
  if (rate.isDropFrame) {
    return smpteDropFrame(nowMs, rate);
  }
  return smpteNonDrop(nowMs, rate);
}

function smpteNonDrop(nowMs: number, rate: FrameRate): SmpteTc {
  const tcFps = Math.round(rate.fps);
  const msToday = ((nowMs % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY;
  const totalFrames = Math.floor((msToday * rate.numerator) / (rate.denominator * 1000));
  return frameNumberToTc(totalFrames, tcFps, ':');
}

function frameNumberToTc(totalFrames: number, tcFps: number, separator: ':' | ';'): SmpteTc {
  const frames = totalFrames % tcFps;
  const totalSeconds = Math.floor(totalFrames / tcFps);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60) % 24;
  return { hours, minutes, seconds, frames, separator };
}

function smpteDropFrame(nowMs: number, rate: FrameRate): SmpteTc {
  // Nominal integer rate for the TC field (30 for 29.97, 60 for 59.94)
  const nominal = Math.round(rate.fps);
  // Number of frame numbers dropped at the start of each affected minute
  const dropFrames = nominal === 60 ? 4 : 2;

  const msToday = ((nowMs % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY;
  const realFrames = Math.floor((msToday * rate.numerator) / (rate.denominator * 1000));

  // Standard SMPTE 12M drop-frame conversion
  // (see https://en.wikipedia.org/wiki/SMPTE_timecode for the canonical algorithm)
  const framesPerMinute = nominal * 60 - dropFrames; // 1798 @ 29.97, 3596 @ 59.94
  const framesPer10Minutes = nominal * 600 - dropFrames * 9; // 17982 @ 29.97, 35964 @ 59.94

  const tenMinBlocks = Math.floor(realFrames / framesPer10Minutes);
  const remainder = realFrames % framesPer10Minutes;

  let displayedFrames = realFrames + dropFrames * 9 * tenMinBlocks;
  if (remainder > dropFrames) {
    displayedFrames += dropFrames * Math.floor((remainder - dropFrames) / framesPerMinute);
  }

  return frameNumberToTc(displayedFrames, nominal, ';');
}

export function formatSmpte(tc: SmpteTc, fps: number): string {
  const pad = (n: number, w = 2) => Math.floor(n).toString().padStart(w, '0');
  const ffWidth = fps >= 100 ? 3 : 2;
  return `${pad(tc.hours)}:${pad(tc.minutes)}:${pad(tc.seconds)}${tc.separator}${pad(tc.frames, ffWidth)}`;
}
